import React, { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

// ---------------------------------------------------------------------------
// Reusable dropdown — click to open, click-outside to close
// ---------------------------------------------------------------------------
const Dropdown = ({ label, children }) => {
    const [open, setOpen] = useState(false)
    const ref = useRef(null)

    useEffect(() => {
        const close = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false)
        }
        document.addEventListener('mousedown', close)
        return () => document.removeEventListener('mousedown', close)
    }, [])

    return (
        <li className="relative" ref={ref}>
            <button
                onClick={() => setOpen(o => !o)}
                className="flex items-center gap-1 hover:text-amber-300 transition-colors focus:outline-none"
                aria-expanded={open}
                aria-haspopup="true"
            >
                {label}
                <svg
                    className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                >
                    <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                    />
                </svg>
            </button>

            {open && (
                <ul
                    className="absolute top-full left-0 mt-2 min-w-48 bg-slate-800 border border-slate-600 rounded-lg shadow-xl z-50 py-1"
                    onClick={() => setOpen(false)}
                >
                    {children}
                </ul>
            )}
        </li>
    )
}

const DropItem = ({ to, children, onClick }) => {
    const cls = 'block w-full text-left px-4 py-2 text-sm text-gray-200 hover:bg-slate-700 hover:text-white transition-colors whitespace-nowrap'

    if (onClick) {
        return (
            <li>
                <button onClick={onClick} className={cls}>{children}</button>
            </li>
        )
    }
    return (
        <li>
            <Link to={to} className={cls}>{children}</Link>
        </li>
    )
}

const DropSeparator = () => <li className="border-t border-slate-600 my-1" aria-hidden="true" />

// ---------------------------------------------------------------------------
// Navbar
// ---------------------------------------------------------------------------
const Navbar = () => {
    const { user, isAuthenticated, signOut } = useAuth()
    const firstName = user?.displayName?.split(' ')[0] || 'Friend'

    return (
        <nav id="mainNav" aria-label="Main navigation" className="bg-slate-800/70 backdrop-blur-sm">
            <div className="flex items-center justify-between px-6 py-3 max-w-7xl mx-auto">

                {/* Primary navigation */}
                <ul className="flex items-center gap-6 text-white text-sm font-medium list-none m-0 p-0">
                    <li>
                        <Link to="/" className="hover:text-amber-300 transition-colors">
                            Home
                        </Link>
                    </li>
                    <li>
                        <Link to="/mission" className="hover:text-amber-300 transition-colors">
                            Mission
                        </Link>
                    </li>

                    <Dropdown label="Resources">
                        <DropItem to="/library">Library</DropItem>
                        <DropItem to="/learn">Learn</DropItem>
                        <DropItem to="/blog">The Path to the Robe</DropItem>
                    </Dropdown>

                    <Dropdown label="Community">
                        <DropItem to="/discord">Discord</DropItem>
                        <DropItem to="/calendar">Calendar</DropItem>
                        <DropItem to="/contact">Contact</DropItem>
                    </Dropdown>

                    <li>
                        <Link
                            to="/donate"
                            className="bg-amber-600 hover:bg-amber-500 text-white px-4 py-1.5 rounded-full font-semibold transition-colors"
                        >
                            Donate
                        </Link>
                    </li>
                </ul>

                {/* Account area */}
                <ul className="flex items-center gap-4 text-white text-sm list-none m-0 p-0">
                    {isAuthenticated && (
                        <Dropdown label={
                            <span className="flex items-center gap-1.5">
                                {user?.avatarUrl ? (
                                    <img
                                        src={user.avatarUrl}
                                        alt={firstName}
                                        className="w-7 h-7 rounded-full object-cover"
                                    />
                                ) : null}
                                {firstName}
                            </span>
                        }>
                            <DropItem to="/profile">Profile</DropItem>
                            <DropItem to="/email-settings">Email Settings</DropItem>
                            {user?.isAdmin && (
                                <>
                                    <DropSeparator />
                                    <DropItem to="/admin">Admin Panel</DropItem>
                                    <DropItem to="/temple/admin">Temple Admin</DropItem>
                                </>
                            )}
                            <DropSeparator />
                            <DropItem onClick={signOut}>Sign Out</DropItem>
                        </Dropdown>
                    )}
                </ul>

            </div>
        </nav>
    )
}

export default Navbar
